import { memo } from "react";

import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
} from "react-router-dom";

import { useAtomValue } from "jotai";

import loadable from "@loadable/component";

import { useInitialAuthValidation } from "./hooks/useInitialAuthValidation";

import { useSessionExpiredListener } from "./hooks/useSessionExpiredListener";

import { SessionExpiredModal } from "./components/SessionExpiredModal";

import { GridPattern } from "./components/ui/grid-pattern";

import {
  authValidationStateAtom,
  isAuthenticatedAtom,
} from "./atoms/auth.atoms";

import {
  goScenesResolvedAtom,
  needsOnboardingAtom,
} from "./atoms/goScenes.atoms";

const RouteLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
  </div>
);

const Login = loadable(() => import("./screens/Login"), {
  fallback: <RouteLoadingFallback />,
});

const ForgotPassword = loadable(() => import("./screens/ForgotPassword"), {
  fallback: <RouteLoadingFallback />,
});

const AuthCallback = loadable(() => import("./screens/AuthCallback"), {
  fallback: <RouteLoadingFallback />,
});

const Home = loadable(() => import("./screens/Home"), {
  fallback: <RouteLoadingFallback />,
});

const Destinations = loadable(() => import("./screens/Destinations"), {
  fallback: <RouteLoadingFallback />,
});

const Integrations = loadable(() => import("./screens/Integrations"), {
  fallback: <RouteLoadingFallback />,
});

const Live = loadable(() => import("./screens/Live"), {
  fallback: <RouteLoadingFallback />,
});

const Settings = loadable(() => import("./screens/Settings"), {
  fallback: <RouteLoadingFallback />,
});

const Recordings = loadable(() => import("./screens/Recordings"), {
  fallback: <RouteLoadingFallback />,
});

const Onboarding = loadable(() => import("./screens/Onboarding"), {
  fallback: <RouteLoadingFallback />,
});

function MobileLayout() {
  useInitialAuthValidation();

  const sessionExpired = useSessionExpiredListener();

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <GridPattern
        width={40}
        height={40}
        className="fixed inset-0 z-0 [mask-image:radial-gradient(700px_circle_at_top,white,transparent)] stroke-primary/20"
      />
      <div className="relative z-10">
        <Outlet />
      </div>
      <SessionExpiredModal open={sessionExpired} />
    </div>
  );
}

/**
 * "/" → cuando hay sesión: /onboarding si nunca eligió diseño, si no /home.
 * Sin sesión → Login (una vez que /auth/me resolvió).
 */

function AuthRedirect() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const validationState = useAtomValue(authValidationStateAtom);

  const needsOnboarding = useAtomValue(needsOnboardingAtom);

  const goScenesResolved = useAtomValue(goScenesResolvedAtom);

  if (validationState === "pending") return <RouteLoadingFallback />;

  if (isAuthenticated) {
    // Esperar a que las go scenes carguen antes de decidir onboarding-vs-home,

    // para no rebotar a /onboarding a un usuario que ya eligió diseño.

    if (!goScenesResolved) return <RouteLoadingFallback />;

    return <Navigate to={needsOnboarding ? "/onboarding" : "/home"} replace />;
  }

  return <Login />;
}

/** Redirects to / (login) if not authenticated */

function RequireAuth() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const validationState = useAtomValue(authValidationStateAtom);

  if (validationState === "pending") return <RouteLoadingFallback />;

  if (!isAuthenticated) return <Navigate to="/" replace />;

  return <Outlet />;
}

/**
 * Gate de onboarding (compone SOBRE RequireAuth). Si el usuario autenticado
 * todavía no eligió diseño → /onboarding. La ruta /onboarding queda FUERA de
 * este gate para no auto-redirigirse a sí misma.
 *
 * Mientras las go scenes cargan (`loading`) y no hay valor cacheado en el atom,
 * mostramos spinner para no parpadear /onboarding → /home.
 */

function RequireOnboarded() {
  const needsOnboarding = useAtomValue(needsOnboardingAtom);

  const goScenesResolved = useAtomValue(goScenesResolvedAtom);

  // Mientras no haya una respuesta (ni cache ni GET terminado) mostramos spinner:

  // evita el parpadeo /home→/onboarding→/home (o quedar atascado en /onboarding).

  if (!goScenesResolved) return <RouteLoadingFallback />;

  if (needsOnboarding) return <Navigate to="/onboarding" replace />;

  return <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <MobileLayout />,

    children: [
      // Public routes

      { path: "/", element: <AuthRedirect /> },

      { path: "/login", element: <Login /> },

      { path: "/forgot-password", element: <ForgotPassword /> },

      { path: "/auth-callback", element: <AuthCallback /> },

      // Protected routes

      {
        element: <RequireAuth />,

        children: [
          // Onboarding: protegido por auth pero FUERA del gate RequireOnboarded

          // (si no, se redirigiría a sí mismo).

          { path: "/onboarding", element: <Onboarding /> },

          {
            element: <RequireOnboarded />,

            children: [
              { path: "/home", element: <Home /> },

              { path: "/destinations", element: <Destinations /> },

              { path: "/integrations", element: <Integrations /> },

              { path: "/live", element: <Live /> },

              { path: "/recordings", element: <Recordings /> },

              { path: "/settings", element: <Settings /> },
            ],
          },
        ],
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default memo(App);
